import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const HeroSection: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // HD images from Unsplash - Office teams, happy people, walking challenges
  const heroImages = [
    {
      url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&h=1080&fit=crop&q=90',
      alt: 'Happy office team collaboration',
    },
    {
      url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1920&h=1080&fit=crop&q=90',
      alt: 'People walking and exercising in workout clothes',
    },
    {
      url: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920&h=1080&fit=crop&q=90',
      alt: 'Happy team doing fitness challenge',
    },
    {
      url: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&h=1080&fit=crop&q=90',
      alt: 'Office team wellness activities',
    },
    {
      url: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&h=1080&fit=crop&q=90',
      alt: 'Happy people in workout clothes',
    },
  ];

  // Rotate images every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* HD Image Background with smooth transitions */}
      <div className="absolute inset-0 w-full h-full">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image.url}
              alt={image.alt}
              className="absolute inset-0 w-full h-full object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </div>
      
      {/* Subtle overlay for text readability - minimal */}
      <div className="absolute inset-0 bg-black/30 z-10"></div>
      
      {/* Frost glass effect overlay */}
      <div className="absolute inset-0 backdrop-blur-[2px] bg-white/5 z-10"></div>
      
      {/* Image indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentImageIndex
                ? 'w-8 bg-white'
                : 'w-2 bg-white/50 hover:bg-white/75'
            }`}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left relative">
            {/* Frost glass background for content */}
            <div className="absolute -inset-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl"></div>
            
            <div className="relative p-6 lg:p-8">
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-white/25 backdrop-blur-lg text-white rounded-full text-sm font-semibold border border-white/40 shadow-lg">
                  Smart Wellness Platform
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
                <span className="block md:whitespace-nowrap">Empower Your Team,</span>
                <span className="block bg-gradient-to-r from-pink-200 to-purple-200 bg-clip-text text-transparent md:whitespace-nowrap">Elevate Performance</span>
              </h1>
              <p className="text-xl text-white/95 mb-8 max-w-2xl mx-auto lg:mx-0 drop-shadow-md">
                Comprehensive wellness platform combining activity tracking, team challenges, and holistic health management.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/contact-sales?source=hero-section"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-purple-600 text-lg font-semibold rounded-lg hover:bg-purple-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
                >
                  Book a Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/15 backdrop-blur-lg text-white text-lg font-semibold rounded-lg border-2 border-white/40 hover:bg-white/25 transition-all shadow-lg"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>

          {/* Right Image/Illustration */}
          <div className="relative">
            <div className="relative z-10">
              <div className="bg-white/15 backdrop-blur-lg rounded-2xl shadow-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-300 border border-white/30">
                <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-xl p-6 border border-white/20">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                        <span className="text-2xl">👟</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Steps Today</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">12,456</p>
                      </div>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-600 to-pink-600 rounded-full" style={{ width: '75%' }}></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Rank</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">#3</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Challenges</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">5</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Teams</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">2</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 w-64 h-64 bg-purple-400/30 rounded-full opacity-50 blur-3xl"></div>
          </div>
        </div>
      </div>

      {/* Floating elements for visual interest */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-32 h-32 bg-pink-400/20 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-40 h-40 bg-purple-400/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>
    </section>
  );
};

export default HeroSection;
